import React, {
  forwardRef,
  useState,
  useImperativeHandle,
  useRef,
} from 'react';
import {
  Pressable,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import {
  List,
  ListItem,
  Input,
  Icon,
  Layout,
  Text,
} from '@ui-kitten/components';
import Popup from 'reactjs-popup';
import usePlacesAutocompleteService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';

import ThemeColor from '../../Theme/colors';
import themeStyle from '../../Theme/styles';
import Button from '../Button';

type MatchedSubstrings = {
  length: number;
  offset: number;
};
type Terms = {
  offset: number;
  value: string;
};

type ResultItem = {
  description: string;
  matched_substrings: Array<MatchedSubstrings>;
  place_id: string;
  reference: string;
  structured_formatting: {
    main_text: string;
    main_text_matched_substrings: Array<MatchedSubstrings>;
    secondary_text: string;
  };
  terms: Array<Terms>;
  types: Array<string>;
};

type GooglePlacesAutocompleteProps = {
  testID: string;
  placeholder?: string;
  initialValue: string;
  iconFill?: string;
  onSelected: (item: ResultItem, result: any) => void;
  style?: StyleProp<ViewStyle>;
  // adjustment for mobile cross platform fix of customization
  renderLeftButton?: () => React.ReactNode;
  renderRightButton?: () => React.ReactNode;
  textInputProps?: {
    InputComp: (props?: any) => React.ReactNode;
    placeholder?: string;
    onRef?: (d?: any) => any;
  };
  inbetweenCompo?: React.ReactNode;
};

const RenderItem = ({
  iconFill,
  onPress,
  ...data
}: ResultItem & { iconFill?: string; onPress: (data: ResultItem) => void }) => {
  const [isHovered, setHovered] = useState(false);
  const shouldHover = isHovered && Boolean(iconFill);
  return (
    <Pressable
      {...(Boolean(iconFill) && {
        onHoverIn: () => setHovered(true),
        onHoverOut: () => setHovered(false),
      })}
    >
      {shouldHover && (
        <View
          style={{
            backgroundColor: iconFill,
            bottom: 0,
            position: 'absolute',
            top: 0,
            width: 8,
            zIndex: 1,
          }}
        />
      )}
      <ListItem
        title={`${data.description}`}
        style={shouldHover && { backgroundColor: ThemeColor.lightGray }}
        onPress={() => onPress(data)}
      />
    </Pressable>
  );
};

const GooglePlaces_Autocomplete = forwardRef(
  (props: GooglePlacesAutocompleteProps, ref) => {
    const {
      renderLeftButton,
      renderRightButton,
      textInputProps,
      inbetweenCompo,
      testID,
      placeholder = 'Address',
      initialValue,
      onSelected,
      style,
      iconFill = ThemeColor.green,
    } = props;

    const inputRef = useRef();
    const { placePredictions, getPlacePredictions, placesService } =
      usePlacesAutocompleteService({
        options: {
          componentRestrictions: { country: 'PH' },
          types: ['geocode', 'establishment'],
        },
      });

    const [value, setValue] = useState(initialValue ? initialValue : '');
    const [showPrediction, setShowPrediction] = useState(false);
    const [width, setWidth] = useState(0);
    useImperativeHandle(ref, () => ({
      _setDefaultSearchValue(value) {
        setTimeout(() => setValue(value), 100);
      },
    }));

    const _onPress = (item: ResultItem) => {
      setShowPrediction(!showPrediction);
      setValue(item?.description);
      placesService?.getDetails(
        {
          placeId: item.place_id,
        },
        (placeDetails) => {
          let convertToString = JSON.stringify(placeDetails); // need to convert first to string so we can get the raw value for lat and lng. if not it will read as constructor
          let result = JSON.parse(convertToString); // revert it to JSON again...
          onSelected(item, result);
        },
      );
    };

    const _onClose = () => {
      setShowPrediction(false);
      getPlacePredictions({ input: '' });
    };
    const _onChangeText = (text: string) => {
      setShowPrediction(true);
      getPlacePredictions({ input: text });
      setValue(text);
    };

    const InputComp = textInputProps?.InputComp
      ? textInputProps?.InputComp
      : null;

    return (
      <Layout
        testID={testID}
        style={[{ flex: 1 }, style]}
        onLayout={({ nativeEvent }) => setWidth(nativeEvent.layout.width)}
      >
        <Popup
          open={showPrediction}
          trigger={() => (
            <View>
              <View style={themeStyle.flexDirectionRow}>
                {renderLeftButton?.()}
                <View style={themeStyle.flex1}>
                  {textInputProps?.InputComp ? (
                    <InputComp
                      onChangeText={_onChangeText}
                      placeholder={textInputProps?.placeholder}
                      onRef={textInputProps?.onRef}
                    />
                  ) : (
                    <Input
                      ref={inputRef}
                      style={styles.search_input}
                      value={value}
                      accessoryLeft={
                        <Icon
                          name="search"
                          fill={iconFill}
                          style={{ width: 14, height: 21 }}
                        />
                      }
                      placeholder={placeholder}
                      onChangeText={_onChangeText}
                    />
                  )}
                </View>
                {renderRightButton?.()}
              </View>
              {inbetweenCompo}
            </View>
          )}
          onClose={_onClose}
          position="bottom center"
          closeOnDocumentClick
          arrow={false}
          contentStyle={{ width: width }}
        >
          <List
            data={placePredictions}
            renderItem={({ item }) => (
              <RenderItem {...item} iconFill={iconFill} onPress={_onPress} />
            )}
            style={styles.listContainer}
          />
        </Popup>
      </Layout>
    );
  },
);

const styles = StyleSheet.create({
  search_input: {
    borderColor: ThemeColor.darkGray,
    borderWidth: 1,
  },
  listContainer: {
    maxHeight: 180,
  },
});
export default GooglePlaces_Autocomplete;
